import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { AppShell } from '../../components/layout/AppShell';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';

export function AnnouncementPage() {
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !title || !message) return;

    setSending(true);
    setError('');

    try {
      // 1. Log the notification
      const { error: logError } = await supabase
        .from('notification_log')
        .insert({ title, message, type: 'admin' });

      if (logError) throw logError;

      // 2. Trigger the edge function to send push notifications
      const { error: fnError } = await supabase.functions.invoke(
        'send-announcement',
        { body: { title, message } }
      );

      // Non-fatal if edge function not deployed yet
      if (fnError) {
        console.warn('Edge function error (non-fatal):', fnError.message);
      }

      setSent(true);
      setTitle('');
      setMessage('');
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong.');
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <PageHeader title="Send Announcement" back />

      <div className="px-4 pt-3 pb-6">
        <Card>
          {sent ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">📢</div>
              <h2 className="font-semibold text-gray-800">Announcement sent!</h2>
              <p className="text-sm text-gray-500 mt-1">
                Members with push notifications will be notified.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setSent(false)}
              >
                Send Another
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                  This will send a push notification to all members who have
                  announcements enabled.
                </p>
              </div>

              <Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Special gathering this week!"
                required
              />

              <Textarea
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your announcement here…"
                rows={4}
                required
              />

              {error && (
                <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2.5 text-sm text-red-600">
                  {error}
                </div>
              )}

              <Button type="submit" fullWidth size="lg" loading={sending}>
                Send Now
              </Button>
            </form>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
